import React from 'react';
import { styled } from 'baseui';
import { Select, Value } from 'baseui/select';
import { Input } from 'baseui/input';
import { Button, KIND, SIZE } from 'baseui/button';
import { LabelMedium, LabelSmall } from 'baseui/typography';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { DashboardFilter } from '../../hooks/useDashboardData';
import { useAccessTypeOptions, useAccessStatusOptions } from '../../hooks/useAccessTypeOptions';

const FilterContainer = styled('div', {
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  marginBottom: '24px',
  overflow: 'hidden',
});

const FilterHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px',
  borderBottom: '1px solid #f3f4f6',
  cursor: 'pointer',
  ':hover': {
    backgroundColor: '#f9fafb',
  },
});

const FilterBody = styled('div', {
  padding: '20px',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
  gap: '16px',
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

const FilterGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const FilterActions = styled('div', {
  display: 'flex',
  gap: '12px',
  padding: '16px 20px',
  borderTop: '1px solid #f3f4f6',
  justifyContent: 'flex-end',
});

const ActiveFiltersContainer = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  padding: '12px 20px',
  borderTop: '1px solid #f3f4f6',
  backgroundColor: '#f9fafb',
});

const FilterChip = styled('div', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 12px',
  backgroundColor: '#e5e7eb',
  borderRadius: '16px',
  fontSize: '13px',
  fontWeight: 500,
});

const ChipRemove = styled('button', {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '2px',
  display: 'flex',
  alignItems: 'center',
  ':hover': {
    opacity: 0.7,
  },
});

interface DashboardFiltersProps {
  filters: DashboardFilter;
  onFiltersChange: (filters: DashboardFilter) => void;
  onApply: () => void;
  onClear: () => void;
  isExpanded?: boolean;
}

const HAS_EVENT_OPTIONS = [
  { label: 'With Event', id: 'true' },
  { label: 'Without Event', id: 'false' },
];

export const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  filters,
  onFiltersChange,
  onApply,
  onClear,
  isExpanded: initialExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(initialExpanded);
  const { options: ACCESS_TYPE_OPTIONS } = useAccessTypeOptions();
  const { options: STATUS_OPTIONS } = useAccessStatusOptions();

  // Update expanded state when prop changes
  React.useEffect(() => {
    setIsExpanded(initialExpanded);
  }, [initialExpanded]);

  const handleAccessTypesChange = (params: { value: Value }) => {
    const selectedIds = params.value.map((item: any) => item.id);
    onFiltersChange({ ...filters, accessTypes: selectedIds.length > 0 ? selectedIds : undefined });
  };

  const handleStatusesChange = (params: { value: Value }) => {
    const selectedIds = params.value.map((item: any) => item.id);
    onFiltersChange({ ...filters, statuses: selectedIds.length > 0 ? selectedIds : undefined });
  };

  const handleHasEventChange = (params: { value: Value }) => {
    if (params.value.length > 0) {
      const value = params.value[0].id === 'true';
      onFiltersChange({ ...filters, hasEvent: value });
    } else {
      const newFilters = { ...filters };
      delete newFilters.hasEvent;
      onFiltersChange(newFilters);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, searchTerm: e.target.value || undefined });
  };

  const handleDateRangeChange = (params: { date: Date | Date[] | null | undefined }) => {
    const dates = params.date;

    if (Array.isArray(dates) && dates.length === 2 && dates[0] && dates[1]) {
      const [start, end] = dates;

      onFiltersChange({
        ...filters,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        timeRange: 'custom',
      });
    } else if (Array.isArray(dates) && dates.length === 1 && dates[0]) {
      // Handle single date selection (user is still selecting)
      // Do nothing, wait for second date
    } else if (!dates || (Array.isArray(dates) && dates.length === 0)) {
      // Clear date filters
      const newFilters = { ...filters };
      delete newFilters.startDate;
      delete newFilters.endDate;
      if (newFilters.timeRange === 'custom') {
        delete newFilters.timeRange;
      }
      onFiltersChange(newFilters);
    }
  };

  const removeFilter = (filterKey: keyof DashboardFilter) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    onFiltersChange(newFilters);
  };

  const getActiveFilters = () => {
    const active: { key: keyof DashboardFilter; label: string }[] = [];

    if (filters.accessTypes && filters.accessTypes.length > 0) {
      const labels = filters.accessTypes.map(id => 
        ACCESS_TYPE_OPTIONS.find(opt => opt.id === id)?.label || `Type ${id}`
      ).join(', ');
      active.push({ key: 'accessTypes', label: `Types: ${labels}` });
    }

    if (filters.statuses && filters.statuses.length > 0) {
      const labels = filters.statuses.map(id =>
        STATUS_OPTIONS.find(opt => opt.id === id)?.label || `Status ${id}`
      ).join(', ');
      active.push({ key: 'statuses', label: `Status: ${labels}` });
    }

    if (filters.hasEvent !== undefined) {
      active.push({ key: 'hasEvent', label: `Event: ${filters.hasEvent ? 'With Event' : 'Without Event'}` });
    }

    if (filters.searchTerm) {
      active.push({ key: 'searchTerm', label: `Search: "${filters.searchTerm}"` });
    }

    if (filters.startDate && filters.endDate) {
      const dayOpts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
      const start = new Date(filters.startDate).toLocaleDateString('en-US', dayOpts);
      const end = new Date(filters.endDate).toLocaleDateString('en-US', dayOpts);
      active.push({ key: 'startDate', label: `Date: ${start} - ${end}` });
    }

    return active;
  };

  const activeFilters = getActiveFilters();

  return (
    <FilterContainer>
      <FilterHeader onClick={() => setIsExpanded(!isExpanded)}>
        <LabelMedium margin={0}>Filters</LabelMedium>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {activeFilters.length > 0 && (
            <LabelSmall color="#6b7280">{activeFilters.length} active</LabelSmall>
          )}
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </FilterHeader>

      {activeFilters.length > 0 && (
        <ActiveFiltersContainer>
          {activeFilters.map(({ key, label }) => (
            <FilterChip key={key}>
              {label}
              <ChipRemove onClick={() => removeFilter(key)}>
                <X size={14} />
              </ChipRemove>
            </FilterChip>
          ))}
        </ActiveFiltersContainer>
      )}

      {isExpanded && (
        <>
          <FilterBody>
            <FilterGroup>
              <LabelSmall>Access Type</LabelSmall>
              <Select
                options={ACCESS_TYPE_OPTIONS}
                value={ACCESS_TYPE_OPTIONS.filter(opt => 
                  filters.accessTypes?.includes(opt.id)
                )}
                onChange={handleAccessTypesChange}
                multi
                placeholder="Select types..."
                size={SIZE.compact}
              />
            </FilterGroup>

            <FilterGroup>
              <LabelSmall>Status</LabelSmall>
              <Select
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.filter(opt =>
                  filters.statuses?.includes(opt.id)
                )}
                onChange={handleStatusesChange}
                multi
                placeholder="Select statuses..."
                size={SIZE.compact}
              />
            </FilterGroup>

            <FilterGroup>
              <LabelSmall>Has Event</LabelSmall>
              <Select
                options={HAS_EVENT_OPTIONS}
                value={
                  filters.hasEvent !== undefined
                    ? HAS_EVENT_OPTIONS.filter(opt => opt.id === String(filters.hasEvent))
                    : []
                }
                onChange={handleHasEventChange}
                placeholder="Filter by event..."
                size={SIZE.compact}
                clearable
              />
            </FilterGroup>

            <FilterGroup>
              <LabelSmall>Search Guest</LabelSmall>
              <Input
                value={filters.searchTerm || ''}
                onChange={handleSearchChange}
                placeholder="Search by name..."
                size={SIZE.compact}
                clearable
              />
            </FilterGroup>
          </FilterBody>

          <FilterActions>
            <Button onClick={onClear} kind={KIND.secondary} size={SIZE.compact}>
              Clear All
            </Button>
            <Button onClick={onApply} size={SIZE.compact}>
              Apply Filters
            </Button>
          </FilterActions>
        </>
      )}
    </FilterContainer>
  );
};
